/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IErrorMessage } from "../../types/components/errorMessage";
import LaddaButton from "../Button/LaddaButton";
import { AppAlert } from "../..";

interface SmsVerificationModalProps {
  show: boolean;
  modalConfig: {
    title?: string;
    bodyContent?: React.JSX.Element;
    confirmBtnName?: string;
    centered?: boolean;
  };
  errorMessage?: IErrorMessage;
  isSendingVerificationCode: boolean;
  isVerifyingCode: boolean;
  isVerififyCodeSuccessful: boolean;
  readyForVerification: boolean;
  sendVerificationRequest: () => void;
  sendVerifyCode: (code: string) => void;
  callbackAction?: () => Promise<void>;
  onClose: () => void;
}

export const SmsVerificationModal: FC<SmsVerificationModalProps> = (
  props: SmsVerificationModalProps
) => {
  const {
    modalConfig,
    isSendingVerificationCode,
    isVerifyingCode,
    readyForVerification,
    isVerififyCodeSuccessful,
    sendVerificationRequest,
    sendVerifyCode,
    onClose,
    callbackAction,
  } = props;
  const { title, bodyContent, confirmBtnName, centered } = modalConfig;
  const [verificationCode, setVerificationCode] = useState("");
  const [enableResendCodeTimer, setEnableResendCodeTimer] = useState(false);
  const [disableResendCodeButton, setDisableResendCodeButton] = useState(true);
  const [resendCodeTimer, setResendCodeTimer] = useState(30);
  const { t } = useTranslation("modal");
  const onResendCode = (e) => {
    e.preventDefault();
    sendVerificationRequest();
  };
  useEffect(() => {
    if (enableResendCodeTimer) {
      const resendCodeInterval = setInterval(() => {
        if (resendCodeTimer > 0) {
          setDisableResendCodeButton(true);
          setResendCodeTimer((prev) => prev - 1);
        } else {
          setEnableResendCodeTimer(false);
          setDisableResendCodeButton(false);
        }
      }, 1000);
      return () => {
        clearInterval(resendCodeInterval);
      };
    }
  }, [enableResendCodeTimer, resendCodeTimer]);

  useEffect(() => {
    setEnableResendCodeTimer(!isSendingVerificationCode);
    if (isSendingVerificationCode) {
      setResendCodeTimer(30);
    }
  }, [isSendingVerificationCode]);

  useEffect(() => {
    if (isVerififyCodeSuccessful) {
      if (callbackAction) {
        callbackAction().then(() => {
          onClose();
        });
      }
    }
  }, [callbackAction, isVerififyCodeSuccessful, onClose]);
  return (
    <Modal
      show={props.show}
      className="simple-modal"
      onHide={onClose}
      centered={centered}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {props.errorMessage && (
          <AppAlert.ErrorAlert error={props.errorMessage} />
        )}
        {!readyForVerification && (
          <div className="verification-request-container">
            {bodyContent}
            {
              <div className="verification-process-container">
                <div className="buttons">
                  <LaddaButton
                    className="btn btn-primary"
                    type="button"
                    btnName={t("patient_phone_verify.send_code")}
                    onClickHandler={() => sendVerificationRequest()}
                    loading={isSendingVerificationCode}
                    disabled={isSendingVerificationCode}
                  />
                </div>
              </div>
            }
          </div>
        )}
        {readyForVerification && (
          <>
            <div className="form-group">
              <label htmlFor="verifcationCode">
                {t("patient_phone_verify.verification_code")}
              </label>
              <input
                type="text"
                className="form-control"
                id="verificationCode"
                placeholder={t("patient_phone_verify.enter_received_code")}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
            </div>
            <div className="buttons">
              <LaddaButton
                className="btn btn-primary mr-2"
                type="button"
                btnName={t("common:general.submit")}
                loading={isVerifyingCode}
                disabled={isVerifyingCode || !verificationCode}
                onClickHandler={() => sendVerifyCode(verificationCode)}
              />
              <Button
                variant="secondary"
                onClick={onResendCode}
                disabled={disableResendCodeButton}
              >
                {t("patient_phone_verify.resend_code")}{" "}
                {enableResendCodeTimer ? `(${resendCodeTimer})` : ""}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ marginTop: 9 }}>
        <Button variant="secondary" onClick={onClose}>
          {t(confirmBtnName || "close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
